import { DeliveryEstimate } from "@redotech/redo-model/conversion/conversion-autocheck";
import { PdpPlacementMethod } from "@redotech/redo-model/conversion/storefront-delivery-estimate";
import { InsertionPosition } from "@redotech/redo-model/widget";
import { assertNever } from "@redotech/util/type";
import { useMemo } from "react";
import { APP_BLOCK_DELIVERY_ESTIMATE_CONTAINER_ID } from "../../app-blocks/delivery-estimate";
import { log } from "../../monitoring/log";
import { RedoDeliveryEstimateContainerElement } from "./element";

const positionMap: Record<InsertionPosition, InsertPosition> = {
  [InsertionPosition.BEFORE]: "beforebegin",
  [InsertionPosition.AFTER]: "afterend",
  [InsertionPosition.PREPEND]: "afterbegin",
  [InsertionPosition.APPEND]: "beforeend",
};

type PdpPortal = {
  foundSelector?: string;
  portal: RedoDeliveryEstimateContainerElement;
};

export const PRODUCT_PAGE_DELIVERY_ESTIMATE_CONTAINER_ID =
  "redo-delivery-estimate-container";
export const STYLE_DISPLAY_UNSET = "unset !important";

export function useRenderPdps({
  deliveryEstimate,
  fromAppBlock,
}: {
  deliveryEstimate: DeliveryEstimate;
  fromAppBlock: boolean;
}): PdpPortal[] {
  const renderPdps = useMemo(() => {
    const pdps: PdpPortal[] = [];

    if (fromAppBlock) {
      return [];
    }

    if (deliveryEstimate.pdpPlacementMethod === PdpPlacementMethod.APP_BLOCK) {
      // Register the custom element if not already registered
      RedoDeliveryEstimateContainerElement.register();

      // Fallback to DOM query for backwards compatibility
      const pdpSelector = `#${APP_BLOCK_DELIVERY_ESTIMATE_CONTAINER_ID}`;
      log("delivery estimate pdpSelector (app block fallback)", pdpSelector);
      const containers = document.querySelectorAll(pdpSelector);
      log("delivery estimate containers (app block fallback)", containers);

      for (const container of containers) {
        const pdpElement = document.createElement(
          RedoDeliveryEstimateContainerElement.TAG,
        ) as RedoDeliveryEstimateContainerElement;
        pdpElement.id = PRODUCT_PAGE_DELIVERY_ESTIMATE_CONTAINER_ID;
        pdpElement.style.display = STYLE_DISPLAY_UNSET;

        // For app blocks, we append directly to the container
        container.append(pdpElement);

        pdps.push({ foundSelector: pdpSelector, portal: pdpElement });
      }
    } else if (
      deliveryEstimate.pdpPlacementMethod === PdpPlacementMethod.THEME_EXTENSION
    ) {
      // For theme extensions, use the configured selectors
      log(
        "delivery estimate pdpSelectors (theme extension)",
        deliveryEstimate.pdpSelectors,
      );

      // Register the custom element if not already registered
      RedoDeliveryEstimateContainerElement.register();

      for (const selectorConfig of deliveryEstimate.pdpSelectors) {
        const containers = document.querySelectorAll(selectorConfig.selector);
        log(
          `delivery estimate containers for selector "${selectorConfig.selector}"`,
          containers,
        );

        for (const container of containers) {
          const pdpElement = document.createElement(
            RedoDeliveryEstimateContainerElement.TAG,
          ) as RedoDeliveryEstimateContainerElement;
          pdpElement.id = PRODUCT_PAGE_DELIVERY_ESTIMATE_CONTAINER_ID;
          pdpElement.style.display = STYLE_DISPLAY_UNSET;

          const insertPosition = positionMap[selectorConfig.insertPosition];
          container.insertAdjacentElement(insertPosition, pdpElement);

          pdps.push({
            foundSelector: selectorConfig.selector,
            portal: pdpElement,
          });
        }
      }
    } else {
      assertNever(deliveryEstimate);
    }

    return pdps;
  }, [deliveryEstimate, fromAppBlock]);

  return renderPdps;
}
